import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import islamService from '../../services/islamService';
import { formatDate } from '../../utils/helpers';

/**
 * Paginated list of published IslamContent from /api/islam/.
 * @param {string} [category] - article | reminder | educational
 * @param {string} [language] - en | ur (API filter)
 * @param {string} [detailBasePath] - default /islam/articles
 */
const IslamContentList = ({
  category,
  language = 'en',
  detailBasePath = '/islam/articles',
  pageSize = 12,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = { page_size: pageSize, language };
        if (category) params.category = category;
        const data = await islamService.getContents(params);
        if (cancelled) return;
        setItems(data?.results ?? []);
      } catch (err) {
        if (!cancelled) {
          console.error('Islam content load failed:', err);
          setError('Unable to load content right now.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [category, language, pageSize]);

  if (loading) {
    return <p className="text-slate-400 text-center py-8">Loading…</p>;
  }

  if (error) {
    return <p className="text-rose-300 text-center py-8">{error}</p>;
  }

  if (items.length === 0) {
    return (
      <p className="rounded-xl border border-white/10 bg-slate-900/40 px-4 py-6 text-center text-slate-400">
        No articles published yet. Check back soon.
      </p>
    );
  }

  return (
    <ul className="space-y-4">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={`${detailBasePath}/${item.slug}`}
            className="block rounded-xl border border-white/10 bg-slate-900/50 p-5 transition hover:border-cyan-400/40 hover:bg-slate-900/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-cyan-300/90">
              <span>{item.category_display || item.category}</span>
              {item.published_date ? (
                <span className="text-slate-500">{formatDate(item.published_date)}</span>
              ) : null}
            </div>
            <h2 className="mt-2 text-lg font-bold text-white">{item.title}</h2>
            <p className="mt-2 text-sm text-slate-400 line-clamp-3">
              {(item.content || '').replace(/\s+/g, ' ').slice(0, 220)}
              {(item.content || '').length > 220 ? '…' : ''}
            </p>
            <span className="mt-3 inline-block text-sm font-medium text-cyan-300">Read more →</span>
          </Link>
        </li>
      ))}
    </ul>
  );
};

export default IslamContentList;
